import { useCallback, useEffect, useRef, type MouseEvent } from "react";

import { Icon } from "../icon/Icon";
import { Popover } from "../popover/Popover";
import { Range } from "../range/Range";
import { CONTROLS_ACTIVE_SECONDS } from "./constants";

import type { RangeChangeEvent } from "../range/Range";
import type { Placement, Strategy } from "@floating-ui/react";

export interface VolumeControlProps {
  classes?: string;
  popoverStrategy?: Strategy;
  popoverPlacement?: Placement;
  popoverOffset?: number;
  value?: number;
  onChange?(volume: number): void;
  onActivate?: () => void;
}
export interface ExpandedVolumeControlProps {
  classes?: string;
  buttonClasses?: string;
  value?: number;
  onChange?(volume: number): void;
}

function getIconName(volume: number = 0.5): string {
  if (volume == 0) return "volume-off";
  if (volume < 0.34) return "volume-low";
  if (volume < 0.67) return "volume";
  return "volume-high";
}

interface VolumeControlRangeProps {
  close?(): void;
  value?: number;
  onActivate?(): void;
  onChange?(volume: number): void;
}

const VolumeControlRange = ({
  close,
  value = 0.5,
  onActivate,
  onChange,
}: VolumeControlRangeProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    onActivate?.();
  }, [onActivate]);

  const handlePointerDown = useCallback((e: MouseEvent) => {
    // Prevent pointer events from affecting behavior of
    // parent elements like the video controls overlay and
    // the sheet modal.
    e.stopPropagation();
  }, []);

  const handleChange = useCallback(
    ({ value }: RangeChangeEvent) => {
      onActivate?.();
      onChange?.(value / 100);
      if (close) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          close();
        }, CONTROLS_ACTIVE_SECONDS * 1000);
      }
    },
    [close, onActivate, onChange, timerRef],
  );

  return (
    <div
      style={{ width: "var(--size-12)" }}
      onPointerDown={handlePointerDown}
      className="VolumeControl--range"
    >
      <Range value={value * 100} onChange={handleChange} />
    </div>
  );
};

export const VolumeControl = ({
  popoverPlacement,
  popoverOffset,
  popoverStrategy,
  classes,
  value,
  onChange,
  onActivate,
}: VolumeControlProps) => {
  return (
    <Popover
      strategy={popoverStrategy}
      placement={popoverPlacement}
      offset={popoverOffset}
      elevation={2}
    >
      <Popover.Trigger>
        <button className={classes}>
          <Icon size="lg" icon={getIconName(value)} variant="solid" />
        </button>
      </Popover.Trigger>

      <Popover.Panel>
        {({ close }) => {
          return (
            <VolumeControlRange
              onActivate={onActivate}
              close={close}
              value={value}
              onChange={onChange}
            />
          );
        }}
      </Popover.Panel>
    </Popover>
  );
};

export const ExpandedVolumeControl = ({
  classes,
  buttonClasses,
  value,
  onChange,
}: ExpandedVolumeControlProps) => {
  return (
    <div className={classes}>
      <button className={buttonClasses}>
        <Icon size="lg" icon={getIconName(value)} variant="solid" />
      </button>
      <VolumeControlRange value={value} onChange={onChange} />
    </div>
  );
};
